import React, { useState, useEffect, useCallback } from 'react';
import { FiSettings, FiFileText, FiBriefcase } from 'react-icons/fi';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function comprimirImagen(file, { maxWidth = 1200, maxHeight = 1200, quality = 0.8 } = {}) {
  const original = await fileToDataUrl(file);
  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = original;
  });

  const ratio = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
  const width = Math.max(1, Math.round(img.width * ratio));
  const height = Math.max(1, Math.round(img.height * ratio));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', quality);
}

export default function AjustesEmpresa() {
  const { usuario, empresa: empresaCtx } = useAuth();
  const esAdmin = usuario?.rol === 'admin' || usuario?.rol === 'superadmin';
  const empresaId = usuario?.empresaId || empresaCtx?._id;

  const [empresa, setEmpresa] = useState({
    nombre: '',
    cif: '',
    direccion: '',
    email: '',
    telefono: '',
    logo: '',
    observacionDefectoPresupuesto: '',
    observacionDefectoFactura: '',
    textoFooterPresupuesto: '',
    textoFooterFactura: '',
  });

  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [subiendoImagen, setSubiendoImagen] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [error, setError] = useState(null);

  const cargarDatosEmpresa = useCallback(async () => {
    if (!empresaId) return;
    setCargando(true);
    setError(null);
    try {
      const { data } = await api.get(`/empresas/${empresaId}`);
      setEmpresa({
        nombre: data.nombre || '',
        cif: data.cif || '',
        direccion: data.direccion || '',
        email: data.email || '',
        telefono: data.telefono || '',
        logo: data.logo || '',
        observacionDefectoPresupuesto: data.observacionDefectoPresupuesto || data.observacionDefecto || '',
        observacionDefectoFactura: data.observacionDefectoFactura || data.observacionDefecto || '',
        textoFooterPresupuesto: data.textoFooterPresupuesto || '',
        textoFooterFactura: data.textoFooterFactura || '',
      });
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudieron cargar los datos de la empresa.');
    } finally {
      setCargando(false);
    }
  }, [empresaId]);

  useEffect(() => {
    cargarDatosEmpresa();
  }, [cargarDatosEmpresa]);

  if (!esAdmin) {
    return (
      <div className="page">
        <h1 className="page-title"><FiSettings style={{ marginRight: 8 }} />Ajustes de Empresa</h1>
        <div className="alert-error" role="alert">No tienes permisos de administración para acceder a esta sección.</div>
      </div>
    );
  }

  const onChangeCampo = (campo, valor) => {
    setEmpresa((prev) => ({ ...prev, [campo]: valor }));
  };

  const onSubirLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendoImagen(true);
    setError(null);
    try {
      const comprimida = await comprimirImagen(file);
      onChangeCampo('logo', comprimida);
    } catch {
      setError('No se pudo procesar la imagen seleccionada.');
    } finally {
      setSubiendoImagen(false);
    }
  };

  const onGuardar = async (e) => {
    e.preventDefault();
    if (!empresa.nombre.trim()) {
      setError('El nombre de la empresa es obligatorio.');
      return;
    }
    const emailLimpio = String(empresa.email || '').trim();
    if (!emailLimpio || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailLimpio)) {
      setError('El email de la empresa es obligatorio y debe tener un formato válido.');
      return;
    }

    setGuardando(true);
    setMensaje(null);
    setError(null);

    try {
      const payload = {
        nombre: empresa.nombre,
        cif: empresa.cif,
        direccion: empresa.direccion,
        email: emailLimpio,
        telefono: empresa.telefono,
        logo: empresa.logo,
        observacionDefectoPresupuesto: empresa.observacionDefectoPresupuesto,
        observacionDefectoFactura: empresa.observacionDefectoFactura,
        textoFooterPresupuesto: empresa.textoFooterPresupuesto,
        textoFooterFactura: empresa.textoFooterFactura,
      };

      await api.put(`/empresas/${empresaId}`, payload);
      setMensaje('Ajustes de la empresa guardados correctamente.');
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudieron guardar los ajustes de la empresa.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header" style={{ marginBottom: 20 }}>
        <div>
          <h1 className="page-title"><FiSettings style={{ marginRight: 8 }} />Ajustes de Empresa</h1>
          <p style={{ color: 'var(--text-soft)', marginTop: 4 }}>
            Configuración básica de tu empresa y personalización de documentos en PDF.
          </p>
        </div>
      </div>

      {mensaje && <div className="alert-ok" style={{ marginBottom: 16 }}>✅ {mensaje}</div>}
      {error && <div className="alert-error" style={{ marginBottom: 16 }}>⚠️ {error}</div>}

      {cargando ? (
        <div className="card" style={{ padding: 24 }}>Cargando datos de la empresa…</div>
      ) : (
        <form onSubmit={onGuardar} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* BLOQUE A: Identidad y Contacto del Negocio */}
          <div className="table-wrap">
            <div style={{ padding: 16, borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiBriefcase style={{ fontSize: 18, color: 'var(--primary)' }} />
              <strong>Bloque A: Identidad y contacto del negocio</strong>
            </div>
            <div style={{ padding: 20 }} className="form-grid">
              <label className="field span-2">
                <span>Nombre comercial de la empresa *</span>
                <input
                  required
                  value={empresa.nombre}
                  onChange={(e) => onChangeCampo('nombre', e.target.value)}
                  placeholder="Ej: Taller Mecánico S.L."
                />
              </label>

              <label className="field">
                <span>CIF / NIF</span>
                <input
                  value={empresa.cif}
                  onChange={(e) => onChangeCampo('cif', e.target.value)}
                  placeholder="B12345678"
                />
              </label>

              <label className="field">
                <span>Email principal *</span>
                <input
                  required
                  type="email"
                  value={empresa.email}
                  onChange={(e) => onChangeCampo('email', e.target.value)}
                  placeholder="info@miempresa.com"
                />
              </label>

              <label className="field span-2">
                <span>Dirección fiscal</span>
                <input
                  value={empresa.direccion}
                  onChange={(e) => onChangeCampo('direccion', e.target.value)}
                  placeholder="Calle Principal 12, Planta 1, 28001 Madrid"
                />
              </label>

              <label className="field">
                <span>Teléfono de contacto</span>
                <input
                  value={empresa.telefono}
                  onChange={(e) => onChangeCampo('telefono', e.target.value)}
                  placeholder="912345678"
                />
              </label>

              <label className="field span-2">
                <span>Logo de la empresa (URL o base64)</span>
                <input
                  value={empresa.logo}
                  onChange={(e) => onChangeCampo('logo', e.target.value)}
                  placeholder="https://... o data:image/..."
                />
              </label>

              <label className="field span-2">
                <span>Subir archivo de logo (se comprime automáticamente)</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={onSubirLogo}
                  disabled={subiendoImagen}
                />
              </label>
            </div>
          </div>

          {/* BLOQUE B: Documentos y Facturación (PDFs) */}
          <div className="table-wrap">
            <div style={{ padding: 16, borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiFileText style={{ fontSize: 18, color: 'var(--primary)' }} />
              <strong>Bloque B: Documentos y facturación (PDFs)</strong>
            </div>
            <div style={{ padding: 20 }} className="form-grid">
              <label className="field span-2">
                <span>Observación por defecto para presupuestos</span>
                <textarea
                  rows={3}
                  value={empresa.observacionDefectoPresupuesto}
                  onChange={(e) => onChangeCampo('observacionDefectoPresupuesto', e.target.value)}
                  placeholder="Ej: Oferta válida durante 15 días. 50% por adelantado..."
                />
                <small className="muted">Se cargará automáticamente al crear un nuevo presupuesto.</small>
              </label>

              <label className="field span-2">
                <span>Observación por defecto para facturas</span>
                <textarea
                  rows={3}
                  value={empresa.observacionDefectoFactura}
                  onChange={(e) => onChangeCampo('observacionDefectoFactura', e.target.value)}
                  placeholder="Ej: Pago mediante transferencia al IBAN ES12 3456 7890..."
                />
                <small className="muted">Se cargará automáticamente al crear una nueva factura.</small>
              </label>

              <label className="field span-2">
                <span>Texto del pie del presupuesto (PDF)</span>
                <textarea
                  rows={3}
                  value={empresa.textoFooterPresupuesto}
                  onChange={(e) => onChangeCampo('textoFooterPresupuesto', e.target.value)}
                  placeholder="Ej: Validez del presupuesto: 30 días."
                />
                <small className="muted">Aparecerá en el pie de página de los presupuestos generados.</small>
              </label>

              <label className="field span-2">
                <span>Texto del pie de la factura (PDF)</span>
                <textarea
                  rows={3}
                  value={empresa.textoFooterFactura}
                  onChange={(e) => onChangeCampo('textoFooterFactura', e.target.value)}
                  placeholder="Ej: Gracias por confiar en nuestros servicios."
                />
                <small className="muted">Aparecerá en el pie de página de las facturas generadas.</small>
              </label>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" className="btn btn-primary" disabled={guardando}>
              {guardando ? 'Guardando…' : 'Guardar ajustes de empresa'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
